/**
 * Commission Tracking Dashboard
 * Comprehensive commission management with rules, splits, and analytics
 */
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

// Component imports
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonButton from 'components/ArgonButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

// Services
import { commissionService, commissionRuleService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

const CommissionDashboard = () => {
  const { user } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [rules, setRules] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    agent_id: '',
    date_range: 'current_month'
  });

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  // Form states
  const [commissionForm, setCommissionForm] = useState({
    transaction_id: '',
    agent_id: '',
    commission_type: 'sale',
    base_amount: '',
    rate: '',
    description: ''
  });

  const [ruleForm, setRuleForm] = useState({
    name: '',
    commission_type: 'sale',
    base_rate: '',
    formula: '',
    conditions: [],
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allCommissions, allRules] = await Promise.all([
        commissionService.getAll(user.uid),
        commissionRuleService.getAll(user.uid),
      ]);

      // Apply client-side filters
      let filtered = allCommissions;
      if (filters.status) {
        filtered = filtered.filter(c => c.status === filters.status);
      }
      if (filters.agent_id) {
        filtered = filtered.filter(c => c.agent_id === filters.agent_id);
      }

      // Derive analytics from commissions
      const paidCommissions = allCommissions.filter(c => c.status === 'paid');
      const paidThisMonth = paidCommissions.filter(c => {
        const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
      const totalAmount = allCommissions.reduce((sum, c) => sum + (c.calculated_amount || 0), 0);
      const paidAmount = paidThisMonth.reduce((sum, c) => sum + (c.calculated_amount || 0), 0);
      const uniqueAgents = new Set(allCommissions.map(c => c.agent_id).filter(Boolean));
      const avgCommission = allCommissions.length > 0 ? totalAmount / allCommissions.length : 0;

      setCommissions(filtered);
      setRules(allRules);
      setAnalytics({
        total_commissions: totalAmount,
        paid_this_month: paidAmount,
        active_agents: uniqueAgents.size,
        average_commission: avgCommission,
      });
    } catch (error) {
      console.error('Error loading commission data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommission = async () => {
    try {
      await commissionService.create(commissionForm, user.uid);
      setOpenCreateDialog(false);
      setCommissionForm({
        transaction_id: '',
        agent_id: '',
        commission_type: 'sale',
        base_amount: '',
        rate: '',
        description: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating commission:', error);
    }
  };

  const handleCreateRule = async () => {
    try {
      await commissionRuleService.create(ruleForm, user.uid);
      setOpenRuleDialog(false);
      setRuleForm({
        name: '',
        commission_type: 'sale',
        base_rate: '',
        formula: '',
        conditions: [],
        is_active: true
      });
      loadData();
    } catch (error) {
      console.error('Error creating rule:', error);
    }
  };

  const handleCalculateCommission = async (commissionId) => {
    try {
      // Calculate commission client-side using rules
      const commission = commissions.find(c => c.id === commissionId);
      if (commission) {
        const calculatedAmount = (commission.base_amount || 0) * ((commission.rate || 0) / 100);
        await commissionService.update(commissionId, {
          calculated_amount: calculatedAmount,
          status: 'calculated',
        });
      }
      loadData();
    } catch (error) {
      console.error('Error calculating commission:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'calculated': return 'info';
      case 'approved': return 'success';
      case 'paid': return 'primary';
      case 'disputed': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <ArgonBox py={3}>
        {/* Analytics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <MoneyIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6" color="textSecondary">
                      Total Commissions
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(analytics.total_commissions)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6" color="textSecondary">
                      Paid This Month
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(analytics.paid_this_month)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <GroupIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6" color="textSecondary">
                      Active Agents
                    </Typography>
                    <Typography variant="h4">
                      {analytics.active_agents || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssessmentIcon color="secondary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6" color="textSecondary">
                      Avg Commission
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(analytics.average_commission)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Controls */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
                  <Typography variant="h5">Commission Management</Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenRuleDialog(true)}
                      sx={{ mr: 2 }}
                    >
                      Add Rule
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenCreateDialog(true)}
                    >
                      New Commission
                    </Button>
                  </Box>
                </Box>

                {/* Filters */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="calculated">Calculated</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="disputed">Disputed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Date Range</InputLabel>
                      <Select
                        value={filters.date_range}
                        label="Date Range"
                        onChange={(e) => setFilters(prev => ({ ...prev, date_range: e.target.value }))}
                      >
                        <MenuItem value="current_month">Current Month</MenuItem>
                        <MenuItem value="last_month">Last Month</MenuItem>
                        <MenuItem value="current_quarter">Current Quarter</MenuItem>
                        <MenuItem value="current_year">Current Year</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Commissions Table */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Commission Records</Typography>
                
                {loading ? (
                  <LinearProgress />
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Transaction</TableCell>
                          <TableCell>Agent</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Base Amount</TableCell>
                          <TableCell>Commission</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commissions.map((commission) => (
                          <TableRow key={commission.id}>
                            <TableCell>
                              <Typography variant="body2">
                                {commission.transaction?.property_address || `Transaction #${commission.transaction_id}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {commission.agent?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={commission.commission_type}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{formatCurrency(commission.base_amount)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(commission.calculated_amount)}
                              </Typography>
                              {commission.rate && (
                                <Typography variant="caption" color="textSecondary">
                                  ({commission.rate}%)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={commission.status}
                                color={getStatusColor(commission.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(commission.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Calculate Commission">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCalculateCommission(commission.id)}
                                  disabled={commission.status !== 'pending'}
                                >
                                  <TrendingUpIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => setSelectedCommission(commission)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton size="small">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Create Commission Dialog */}
        <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Commission</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Transaction ID"
                  value={commissionForm.transaction_id}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, transaction_id: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Agent ID"
                  value={commissionForm.agent_id}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, agent_id: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Commission Type</InputLabel>
                  <Select
                    value={commissionForm.commission_type}
                    label="Commission Type"
                    onChange={(e) => setCommissionForm(prev => ({ ...prev, commission_type: e.target.value }))}
                  >
                    <MenuItem value="sale">Sale</MenuItem>
                    <MenuItem value="rental">Rental</MenuItem>
                    <MenuItem value="management">Management</MenuItem>
                    <MenuItem value="referral">Referral</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Base Amount"
                  type="number"
                  value={commissionForm.base_amount}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, base_amount: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rate (%)"
                  type="number"
                  value={commissionForm.rate}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, rate: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={commissionForm.description}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCommission} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Create Rule Dialog */}
        <Dialog open={openRuleDialog} onClose={() => setOpenRuleDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create Commission Rule</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rule Name"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Commission Type</InputLabel>
                  <Select
                    value={ruleForm.commission_type}
                    label="Commission Type"
                    onChange={(e) => setRuleForm(prev => ({ ...prev, commission_type: e.target.value }))}
                  >
                    <MenuItem value="sale">Sale</MenuItem>
                    <MenuItem value="rental">Rental</MenuItem>
                    <MenuItem value="management">Management</MenuItem>
                    <MenuItem value="referral">Referral</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Base Rate (%)"
                  type="number"
                  value={ruleForm.base_rate}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, base_rate: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Formula"
                  placeholder="e.g., base_amount * (rate / 100)"
                  value={ruleForm.formula}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, formula: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRuleDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRule} variant="contained">Create Rule</Button>
          </DialogActions>
        </Dialog>
      </ArgonBox>
      
      <Footer />
    </DashboardLayout>
  );
};

export default CommissionDashboard;